import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { FixedSizeList, VariableSizeList } from 'react-window';
import { Button, Switch } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ProductRow from './ProductRow';
import { ALL } from '../../../../Containers/Partners/Components/ProductsSelector/Components/ProductsSelectorModal/utils';
import {
	getProductsList,
	getMenuSelected,
	getCategorySelected,
	getConfiguredByProduct,
	getExportProductIds,
	parseCategoryByMenu,
	PSEUDO,
} from './selector';

// hauteur du viewport virtualisé (la colonne scrolle à l'intérieur)
const LIST_HEIGHT = 520;
const ITEM_H = 52;
const HEADER_H = 38;
const NONE_KEY = '__none__';

// Colonne centrale : liste des produits du menu courant, filtrée par catégorie
// et par recherche. Virtualisée (react-window) pour tenir de gros catalogues.
// En pseudo-catégorie (exports / non configurés), la liste est groupée par
// sous-catégorie (catégorie ou type de produit, au choix via le toggle).
class ProductList extends React.Component {
	// `input` = valeur affichée (immédiate) ; `search` = terme filtré (débouncé)
	// `groupBy` = mode de regroupement en pseudo-catégorie
	state = { input: '', search: '', groupBy: 'category' };

	componentWillUnmount() {
		clearTimeout(this.searchTimeout);
	}

	onSearchChange = e => {
		const value = e.target.value;
		this.setState({ input: value });
		clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(
			() => this.setState({ search: value }),
			300
		);
	};

	setGroupBy = value => this.setState({ groupBy: value });

	isGrouped() {
		const { categorySelected } = this.props;
		return (
			categorySelected === PSEUDO.EXPORTS ||
			categorySelected === PSEUDO.UNCONFIGURED
		);
	}

	getItems() {
		const {
			products,
			menuSelected,
			categorySelected,
			exportProductIds,
			configuredByProduct,
		} = this.props;
		const byMenu = products[menuSelected] || {};
		let items = Object.values(byMenu).filter(p => p && p.type !== 'menu');

		if (categorySelected === PSEUDO.EXPORTS) {
			// pseudo-catégorie : uniquement les produits présents dans les exports
			items = items.filter(p => exportProductIds[String(p.id)]);
		} else if (categorySelected === PSEUDO.UNCONFIGURED) {
			// pseudo-catégorie : uniquement les produits sans aucune règle
			items = items.filter(p => !configuredByProduct[String(p.id)]);
		} else if (categorySelected !== ALL) {
			if (categorySelected === 0) {
				items = items.filter(p => !p.categories || p.categories.length === 0);
			} else {
				items = items.filter(p =>
					(p.categories || []).some(c => String(c) === String(categorySelected))
				);
			}
		}

		const search = this.state.search.trim().toLowerCase();
		if (search) {
			items = items.filter(p => (p.name || '').toLowerCase().includes(search));
		}

		return items;
	}

	// Construit les groupes (sous-catégories) selon le mode courant.
	// En mode catégorie un produit multi-catégories apparaît dans chacune.
	buildGroups(items) {
		const { groupBy } = this.state;
		const { categories } = this.props;
		const map = new Map();
		const push = (key, label, item) => {
			if (!map.has(key)) {
				map.set(key, { key, label, items: [] });
			}
			map.get(key).items.push(item);
		};

		items.forEach(item => {
			if (groupBy === 'product_type') {
				const key =
					item.id_product_type != null ? String(item.id_product_type) : NONE_KEY;
				const label =
					item.product_type_name || I18n.t('settings.binding.no_type');
				push(key === NONE_KEY ? NONE_KEY : key, label, item);
			} else {
				const cats = item.categories || [];
				if (!cats.length) {
					push(NONE_KEY, I18n.t('settings.binding.no_category'), item);
				} else {
					cats.forEach(c => {
						const cat = categories[c] || categories[String(c)];
						const label = cat
							? cat.name
							: I18n.t('settings.binding.no_category');
						push(String(c), label, item);
					});
				}
			}
		});

		const groups = Array.from(map.values());
		groups.sort((a, b) => {
			if (a.key === NONE_KEY) return 1;
			if (b.key === NONE_KEY) return -1;
			return (a.label || '').localeCompare(b.label || '');
		});
		return groups;
	}

	// Aplatit les groupes en lignes (header + produits) pour la liste virtualisée.
	flattenRows(groups) {
		const rows = [];
		groups.forEach(group => {
			rows.push({
				type: 'header',
				key: 'h-' + group.key,
				label: group.label,
				count: group.items.length,
			});
			group.items.forEach(item => {
				rows.push({ type: 'item', key: group.key + '-' + item.id, product: item });
			});
		});
		return rows;
	}

	renderProduct(item, style) {
		const { selectionMode, selectedIds, onToggleSelect, onOpenProduct } =
			this.props;
		const conf = this.props.configuredByProduct[String(item.id)];
		return (
			<div style={style}>
				<ProductRow
					id={item.id}
					name={item.name}
					configured={!!conf}
					count={conf ? conf.total : 0}
					selectable={selectionMode}
					selected={!!selectedIds[String(item.id)]}
					onClick={selectionMode ? onToggleSelect : onOpenProduct}
				/>
			</div>
		);
	}

	renderFlatRow = items => ({ index, style }) =>
		this.renderProduct(items[index], style);

	renderGroupedRow = rows => ({ index, style }) => {
		const row = rows[index];
		if (row.type === 'header') {
			return (
				<div style={style} className="binding-group-header">
					<span className="binding-group-title">{row.label}</span>
					<span className="binding-group-count">{row.count}</span>
				</div>
			);
		}
		return this.renderProduct(row.product, style);
	};

	renderList(items) {
		if (!this.isGrouped()) {
			return (
				<FixedSizeList
					width={'100%'}
					height={LIST_HEIGHT}
					itemCount={items.length}
					itemSize={ITEM_H}
					overscanCount={12}>
					{this.renderFlatRow(items)}
				</FixedSizeList>
			);
		}

		const rows = this.flattenRows(this.buildGroups(items));
		// key : force le remount pour réinitialiser le cache de tailles quand la
		// composition des lignes change (mode, catégorie, recherche).
		const signature = `${this.state.groupBy}|${this.props.categorySelected}|${this.state.search}|${rows.length}`;
		return (
			<VariableSizeList
				key={signature}
				width={'100%'}
				height={LIST_HEIGHT}
				itemCount={rows.length}
				itemSize={index => (rows[index].type === 'header' ? HEADER_H : ITEM_H)}
				overscanCount={12}>
				{this.renderGroupedRow(rows)}
			</VariableSizeList>
		);
	}

	render() {
		const items = this.getItems();
		const {
			selectionMode,
			selectedIds,
			onToggleSelectionMode,
			onConfigureSelection,
		} = this.props;
		const selectedCount = Object.keys(selectedIds || {}).length;

		return (
			<React.Fragment>
				<div className="section-products-header">
					<h2>{I18n.t('settings.binding.products_title')}</h2>
				</div>
				<div className="binding-search-row">
					<div className="binding-search">
						<SearchIcon fontSize="small" />
						<input
							type="text"
							placeholder={I18n.t('settings.binding.search')}
							value={this.state.input}
							onChange={this.onSearchChange}
						/>
					</div>
					<label className="binding-select-toggle">
						<Switch
							color="primary"
							checked={selectionMode}
							onChange={onToggleSelectionMode}
						/>
						<span>{I18n.t('settings.binding.select_mode')}</span>
					</label>
				</div>

				{selectionMode ? (
					<div className="binding-select-bar">
						<span className="binding-select-count">
							{I18n.t('settings.binding.selected_count', {
								count: selectedCount,
							})}
						</span>
						<Button
							size="small"
							color="primary"
							variant="contained"
							disabled={selectedCount === 0}
							onClick={onConfigureSelection}>
							{I18n.t('settings.binding.configure_selection')}
						</Button>
					</div>
				) : null}

				{this.isGrouped() ? (
					<div className="binding-groupby">
						<span className="binding-groupby-label">
							{I18n.t('settings.binding.group_label')}
						</span>
						<div className="binding-groupby-switch">
							<span
								className={
									this.state.groupBy === 'category' ? 'is-active' : ''
								}>
								{I18n.t('settings.binding.group_by_category')}
							</span>
							<Switch
								color="primary"
								checked={this.state.groupBy === 'product_type'}
								onChange={e =>
									this.setGroupBy(e.target.checked ? 'product_type' : 'category')
								}
							/>
							<span
								className={
									this.state.groupBy === 'product_type' ? 'is-active' : ''
								}>
								{I18n.t('settings.binding.group_by_type')}
							</span>
						</div>
					</div>
				) : null}

				<div className="binding-product-list">
					{items.length ? (
						this.renderList(items)
					) : (
						<p className="binding-empty">
							{I18n.t('settings.binding.no_product')}
						</p>
					)}
				</div>
			</React.Fragment>
		);
	}
}

ProductList.propTypes = {
	onOpenProduct: PropTypes.func.isRequired,
	// mode sélection multiple
	selectionMode: PropTypes.bool,
	selectedIds: PropTypes.object,
	onToggleSelectionMode: PropTypes.func,
	onToggleSelect: PropTypes.func,
	onConfigureSelection: PropTypes.func,
};

const mapStateToProps = state => ({
	products: getProductsList(state),
	menuSelected: getMenuSelected(state),
	categorySelected: getCategorySelected(state),
	configuredByProduct: getConfiguredByProduct(state),
	exportProductIds: getExportProductIds(state),
	categories: parseCategoryByMenu(state),
});

export default connect(mapStateToProps)(ProductList);
